<template>
  <div>
    <div class="mt-6" v-if="!isGlobalPolicy && localValue.selectionType">
      <div class="subtitle1 text-primary mb-4">
        {{ $t("emailSettings.blockedEmailReportsConfig.selectUsers") }}
      </div>
      <button-toggle-group
        v-model="localValue.selectionType"
        :items="selectionTypeItems"
        :disabled="disabled"
        data-testid="selection-type-btn-toggle"
      />
    </div>

    <div v-if="context === EmailReportConfigContext.BLOCKED_EMAIL_REPORTS" class="mt-6">
      <div class="subtitle1 text-primary mb-4">
        {{ $t("emailSettings.blockedEmailReportsConfig.selectFrequency") }}
      </div>
      <button-toggle-group
        v-model="(localValue as BlockedEmailReportsConfig).frequency!"
        :items="frequencyItems"
        :disabled="disabled"
        data-testid="frequency-toggle"
      />
    </div>

    <template
      v-if="localValue.selectionType === PhishingReportSelectionType.SPECIFIC && !isGlobalPolicy"
    >
      <div class="mt-6 mb-4">
        <v-checkbox
          v-model="localValue.labelsEnabled"
          class="mb-2"
          :disabled="disabled"
          data-testid="labels-enabled-checkbox"
        >
          <template #label>
            <span class="subtitle2">{{
              $t("emailSettings.blockedEmailReportsConfig.userLabels")
            }}</span>
          </template>
        </v-checkbox>

        <v-autocomplete
          v-model="localValue.labels"
          :items="labels"
          :label="$t('emailSettings.blockedEmailReportsConfig.userLabels')"
          :placeholder="$t('emailSettings.blockedEmailReportsConfig.enterUserLabels')"
          variant="outlined"
          multiple
          return-object
          chips
          closable-chips
          clear-on-select
          data-testid="labels-autocomplete"
          item-value="id"
          item-title="name"
          :disabled="disabled || !localValue.labelsEnabled"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              :closable="true"
              variant="flat"
              size="default"
              close-icon="$closeCircle"
              :text="item.raw.name"
              class="label-chip"
              label
              rounded="3"
              :color="item.raw.color"
            >
              <div class="d-flex align-center">
                <span
                  class="ml-1 mr-2 coro-label text-primary text-uppercase"
                  v-text="item.title"
                />
              </div>
            </v-chip>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <template #default>
                <div class="d-flex align-center label-popup-block">
                  <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                  <span v-text="item.raw.name" />
                </div>
              </template>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>

      <div class="mb-4">
        <v-checkbox
          v-model="localValue.groupsEnabled"
          class="mb-2"
          :disabled="disabled"
          data-testid="groups-enabled-checkbox"
        >
          <template #label>
            <span class="subtitle2">{{
              $t("emailSettings.blockedEmailReportsConfig.protectedGroups")
            }}</span>
          </template>
        </v-checkbox>

        <v-autocomplete
          v-model="localValue.groups"
          data-testid="groups-autocomplete"
          :loading="groupsLoading"
          :items="groups"
          :label="capitalize($t('general.group', 0))"
          :placeholder="$t('emailSettings.blockedEmailReportsConfig.enterGroupNames')"
          variant="outlined"
          chips
          closable-chips
          multiple
          no-filter
          return-object
          clear-on-select
          :hide-no-data="hideGroupsNoData"
          item-value="groupId"
          :no-data-text="$t('modals.addNewGroup.noData')"
          v-model:search="currentGroupInputValue"
          :disabled="disabled || !localValue.groupsEnabled"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              :closable="true"
              variant="flat"
              size="default"
              close-icon="$closeCircle"
              :text="item.title"
              color="indigo-faint"
            >
              <div class="d-flex align-center">
                <coro-icon class="service-icon-sm" :icon-name="item.raw.service"></coro-icon>
                <span class="ml-1 mr-2">{{ item.raw.name }}</span>
              </div>
            </v-chip>
          </template>
          <template #item="{ props, item }">
            <v-list-item v-bind="props" title="">
              <div class="d-flex align-center" :data-testid="`item-${camelCase(item.raw.service)}`">
                <coro-icon class="service-icon" :icon-name="item.raw.service"></coro-icon>
                <span class="ml-1">{{ item.raw.name }}</span>
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>

      <div>
        <v-checkbox
          v-model="localValue.usersEnabled"
          class="mb-2"
          :disabled="disabled"
          data-testid="users-enabled-checkbox"
        >
          <template #label>
            <span class="subtitle2">{{
              $t("emailSettings.blockedEmailReportsConfig.specificUsers")
            }}</span>
          </template>
        </v-checkbox>

        <v-autocomplete
          v-model="localValue.users"
          :loading="usersLoading"
          :items="users"
          :label="$t('general.users')"
          :placeholder="$t('emailSettings.blockedEmailReportsConfig.enterEmailAddresses')"
          variant="outlined"
          return-object
          chips
          closable-chips
          multiple
          no-filter
          :no-data-text="$t('general.noData')"
          data-testid="users-autocomplete"
          :hide-no-data="hideUsersNoData"
          item-title="email"
          item-value="email"
          v-model:search="currentUserInputValue"
          :disabled="disabled || !localValue.usersEnabled"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              :closable="true"
              variant="flat"
              size="default"
              color="indigo-faint"
              close-icon="$closeCircle"
              :text="item.title"
            >
              <div class="d-flex align-center">
                <span class="ml-1 mr-1">{{ item.title }}</span>
              </div>
            </v-chip>
          </template>
          <template #item="{ props, item }">
            <v-list-item v-bind="props" title="">
              <div class="d-flex align-center">
                <span class="ml-1">{{ item.raw.email }}</span>
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType, onMounted, ref, toRefs, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  PhishingReportSelectionType,
  PhishingReportFrequency,
  EmailReportConfigContext,
} from "@/constants/emails";
import CoroIcon from "@/components/CoroIcon.vue";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";
import {
  type AutoForwardingBlockingPolicyConfig,
  type BlockedEmailReportsConfig,
  type BlockedEmailReportUser,
  useEmailSettingsStore,
} from "@/_store/email-security/email-settings.module";
import { useUserLabelsStore } from "@/_store/users-settings/user-labels.module.ts";
import { storeToRefs } from "pinia";
import { capitalize, camelCase } from "lodash";
import {
  useGroupsSettingsTabStore,
  type UserGroupSettingsItem,
} from "@/_store/users-settings/user-groups-tab.module.ts";
import uniqBy from "lodash/uniqBy";
import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  components: {
    CoroIcon,
    ButtonToggleGroup,
  },
  props: {
    modelValue: {
      type: Object as PropType<BlockedEmailReportsConfig | AutoForwardingBlockingPolicyConfig>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    context: {
      type: String,
      default: "",
    },
    isGlobalPolicy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const userLabelsStore = useUserLabelsStore();
    const userGroupsStore = useGroupsSettingsTabStore();
    const emailSettingsStore = useEmailSettingsStore();
    let timer: ReturnType<typeof setTimeout>;
    const currentGroupInputValue = ref("");
    const currentUserInputValue = ref("");
    const groups = ref<UserGroupSettingsItem[]>([]);
    const users = ref<BlockedEmailReportUser[]>([]);
    const groupsLoading = ref(false);
    const usersLoading = ref(false);
    const { labels } = storeToRefs(userLabelsStore);
    const localValue = useTwoWayBinding(toRefs(props).modelValue, (value) => {
      emit("update:modelValue", value);
    });
    const selectionTypes = [PhishingReportSelectionType.ALL, PhishingReportSelectionType.SPECIFIC];
    const frequencies = [
      PhishingReportFrequency.DAILY,
      PhishingReportFrequency.EVERY_3_DAYS,
      PhishingReportFrequency.WEEKLY,
    ];

    const selectionTypeItems = computed<ButtonToggleItem[]>(() =>
      selectionTypes.map((type) => ({
        value: type,
        label: t(`emailSettings.blockedEmailReportsConfig.selectionType.${type}`),
      }))
    );

    const frequencyItems = computed<ButtonToggleItem[]>(() =>
      frequencies.map((frequency) => ({
        value: frequency,
        label: t(`emailSettings.blockedEmailReportsConfig.${frequency}`),
      }))
    );

    const hideGroupsNoData = computed(() => {
      return groupsLoading.value
        ? true
        : !currentGroupInputValue.value && groups.value.length === 0;
    });

    const hideUsersNoData = computed(() => {
      return usersLoading.value ? true : !currentUserInputValue.value && users.value.length === 0;
    });

    onMounted(async () => {
      await userLabelsStore.getUserLabels();
    });

    watch(currentGroupInputValue, async (val: string) => {
      clearTimeout(timer);
      groupsLoading.value = true;
      timer = setTimeout(async () => {
        try {
          if (!val || val.length < 3) {
            return;
          }
          const response = await userGroupsStore.searchUserGroups(val);
          groups.value = uniqBy([...(localValue.value.groups ?? []), ...response.data], "groupId");
        } catch (error) {
          console.error("Error searching user groups:", error);
        } finally {
          groupsLoading.value = false;
        }
      }, 500);
    });

    watch(currentUserInputValue, async (val: string) => {
      clearTimeout(timer);
      usersLoading.value = true;
      timer = setTimeout(async () => {
        try {
          if (!val || val.length < 3) {
            return;
          }
          const response = await emailSettingsStore.searchUsers(val);
          users.value = uniqBy([...(localValue.value.users ?? []), ...response], "userId");
        } catch (error) {
          console.error("Error searching users:", error);
        } finally {
          usersLoading.value = false;
        }
      }, 500);
    });

    return {
      PhishingReportSelectionType,
      localValue,
      labels,
      groups,
      users,
      usersLoading,
      groupsLoading,
      currentGroupInputValue,
      currentUserInputValue,
      hideGroupsNoData,
      hideUsersNoData,
      selectionTypes,
      selectionTypeItems,
      frequencyItems,
      capitalize,
      camelCase,
      EmailReportConfigContext,
    };
  },
});
</script>

<style lang="scss" scoped>
.service-icon {
  width: 40px;
  height: 40px;
}

.service-icon-sm {
  width: 25px;
  height: 25px;
}

:deep(*) {
  .v-field .v-chip {
    --v-chip-height: 32px;
  }
}
</style>
